import { useMemo } from 'react';
import { ArrowLeft, Printer } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useReportData } from '@/hooks/useReportData';

// All components
import ReportHeader from '@/components/report/ReportHeader';
import ScoreCard from '@/components/report/ScoreCard';
import VulnerabilitySummary from '@/components/report/VulnerabilitySummary';
import KeyFinding from '@/components/report/KeyFinding';
import PluginProfile from '@/components/report/PluginProfile';
import CreatorVerification from '@/components/report/CreatorVerification';
import ScoreBreakdown from '@/components/report/ScoreBreakdown';
import RiskHeatmap from '@/components/report/RiskHeatmap';
import VulnerabilityGroup, { groupVulnerabilities } from '@/components/report/VulnerabilityGroup';
import ThreatModeling from '@/components/report/ThreatModeling';
import MCPToolsSummary from '@/components/report/MCPToolsSummary';
import PermissionScope from '@/components/report/PermissionScope';
import PrioritizedRemediation from '@/components/report/PrioritizedRemediation';
import RiskAcceptanceMatrix from '@/components/report/RiskAcceptanceMatrix';
import RecommendationCard from '@/components/report/RecommendationCard';
import ReportFooter from '@/components/report/ReportFooter';
import RFIASection from '@/components/report/RFIASection';
import { PageSpinner, PageEmptyState } from '@/components/ui/PageLayout';
import { ReportSubHeader } from '@/components/report/ReportSubHeader';
import { SectionVisibility } from '@/components/report/ReportSettings';
import { ToolDetail } from '@/components/report/ToolDetailDialog';
import { sampleReport, toolDetails as staticToolDetails } from '@/data/sampleReportData';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import type { ThreatModeling as ThreatModelingData, RFIAFindingData, RFIAAggregateData, KeyFinding as KeyFindingType } from '@/utils/scanToInsights';
import { buildHeatmapFromScores, type HeatmapFromScoresResult } from '@/utils/scanToInsights';

// =============================================================================
// Extended report view type — mirrors ReportPage.tsx's ReportViewData
// =============================================================================

type HeatmapShape = HeatmapFromScoresResult;

/**
 * Adds optional API fields that exist on dynamic scan data but aren't present
 * on the static sampleReport. Eliminates `as any` casts throughout the page.
 */
type ReportViewData = typeof sampleReport & {
  branch?: string;
  githubTokenPresent?: boolean;
  prioritizedRemediation?: Array<{ id: string; title: string; description: string; riskLevel: string; priority: string; effort: string; estimatedTime: string; category?: string }>;
  riskAcceptance?: Array<{ id: string; title: string; description: string; riskLevel: string; justification: string; owner: string; reviewDate: string; status?: string }>;
  remediationItems: Array<{ id: string; title: string; description: string; riskLevel: string; priority: string; effort: string; estimatedTime: string; category?: string }>;
  riskAcceptanceItems: Array<{ id: string; title: string; description: string; riskLevel: string; justification: string; owner: string; reviewDate: string; status?: string }>;
  rfiaFindings?: RFIAFindingData[];
  rfiaAggregate?: RFIAAggregateData;
  scores: typeof sampleReport['scores'] & {
    crs?: { score: number; level: 'critical' | 'high' | 'medium' | 'low' };
  };
  threatModeling: ThreatModelingData & {
    threatActors: typeof sampleReport['threatModeling']['threatActors'];
    attackVectors: typeof sampleReport['threatModeling']['attackVectors'];
  };
  heatmapData: HeatmapShape;
};

// =============================================================================
// Section visibility — read from sessionStorage (written by the main report page)
// =============================================================================

const DEFAULT_VISIBILITY: SectionVisibility = {
  executiveSummary: true,
  keyFindings: true,
  pluginProfile: true,
  scoringBreakdown: true,
  creatorVerification: true,
  aiBom: false,
  vulnerabilityAssessment: true,
  threatModeling: true,
  permissionScope: true,
  prioritizedRemediation: true,
  riskAcceptanceMatrix: true,
  recommendation: true,
  rfiaSection: true,
  vibeguardSection: true,
  complianceCrosswalk: false,
  heatmapAivss: true,
  heatmapCvss: true,
  heatmapAars: true,
  heatmapOverall: true,
  heatmapVulnerability: true,
};

function readVisibilityFromStorage(): SectionVisibility {
  try {
    const raw = sessionStorage.getItem('reportVisibility');
    if (raw) {
      return { ...DEFAULT_VISIBILITY, ...JSON.parse(raw) };
    }
  } catch {
    // ignore parse errors
  }
  return DEFAULT_VISIBILITY;
}

// =============================================================================
// Component
// =============================================================================

const PrintableReportPage = () => {
  useDocumentTitle('Print Report');
  // Use the same hook as ReportPage so both pages share the same data pipeline
  const { data: reportDataResult, loading } = useReportData();

  // Same useMemo transform as ReportPage.tsx
  const reportData = useMemo((): ReportViewData => {
    if (!reportDataResult?.report) return sampleReport as ReportViewData;

    // InsightsReport → ReportViewData: runtime data includes alias fields not on the canonical interface
    const realData = reportDataResult.report as unknown as ReportViewData;

    return {
      reportId: realData.reportId || '',
      reportDate: realData.reportDate || '',
      pluginName: realData.pluginName || '',
      classification: realData.classification || 'Confidential',
      generatedAt: realData.generatedAt || new Date(),
      scores: realData.scores || {
        aivss: { score: 0, level: 'low' as const },
        cvss: { score: 0, level: 'low' as const },
        aars: { score: 0, level: 'low' as const },
        creatorTrust: { score: 0, level: 'low' as const },
      },
      scoreBreakdowns: realData.scoreBreakdowns || { aivss: [], cvss: [], aars: [] },
      mcpTools: realData.mcpTools || {
        totalTools: 0,
        highRiskTools: 0,
        mediumRiskTools: 0,
        lowRiskTools: 0,
        toolsList: [],
        toolsInfo: {},
      },
      vulnerabilities: realData.vulnerabilities || {
        critical: 0,
        high: 0,
        medium: 0,
        low: 0,
        info: 0,
      },
      keyFindings: realData.keyFindings || [],
      creator: realData.creator || {
        name: 'Unknown',
        organization: 'Unknown',
        verified: false,
        country: 'Unknown',
        organizationType: 'Unknown',
        trustScore: 0,
      },
      plugin: realData.plugin || {
        name: '',
        description: '',
        version: '',
        location: '',
        category: '',
        license: 'Unknown',
      },
      vulnerabilityDetails: realData.vulnerabilityDetails || [],
      permissions: realData.permissions || [],
      recommendation: realData.recommendation || { type: 'conditional', summary: '', conditions: [] },
      threatModeling: realData.threatModeling || { threatActors: [], attackVectors: [] },
      // CRITICAL: Must come from real data only — never from sample
      remediationItems: realData.remediationItems || realData.prioritizedRemediation || [],
      riskAcceptanceItems: realData.riskAcceptanceItems || realData.riskAcceptance || [],
      // RFIA passthrough
      rfiaFindings: realData.rfiaFindings,
      rfiaAggregate: realData.rfiaAggregate,
      heatmapData:
        realData.heatmapData ||
        buildHeatmapFromScores(realData.scoreBreakdowns, realData.vulnerabilityDetails),
    } as ReportViewData;
  }, [reportDataResult]);

  // Auto-generate toolDetails from permissions — identical to ReportPage.tsx
  const toolDetailsData = useMemo(() => {
    const merged: Record<string, ToolDetail> = {
      ...staticToolDetails,
      ...(reportDataResult?.toolDetails || {}),
    };
    const riskRank: Record<string, number> = { critical: 4, high: 3, medium: 2, low: 1, info: 0 };
    const perms = reportDataResult?.report?.permissions;
    if (perms && Array.isArray(perms)) {
      for (const perm of perms) {
        for (const tool of perm.tools || []) {
          if (!merged[tool]) {
            merged[tool] = {
              name: tool,
              description: `This tool provides ${perm.capability.toLowerCase()} functionality.`,
              riskLevel: perm.riskLevel as ToolDetail['riskLevel'],
              riskReason: perm.riskJustification || `Classified as ${perm.riskLevel} risk.`,
              capabilities: [perm.capability],
            };
          } else {
            const existing = merged[tool];
            if (!existing.capabilities.includes(perm.capability)) {
              existing.capabilities.push(perm.capability);
            }
            if ((riskRank[perm.riskLevel] || 0) > (riskRank[existing.riskLevel] || 0)) {
              existing.riskLevel = perm.riskLevel as ToolDetail['riskLevel'];
              existing.riskReason = perm.riskJustification || existing.riskReason;
            }
          }
        }
      }
    }
    return merged;
  }, [reportDataResult]);

  // Read visibility settings stored by the main report page before opening print tab
  const sectionVisibility = useMemo(() => readVisibilityFromStorage(), []);

  // GRTTL4C-122: memoize groupVulnerabilities — avoids rebuilding Map + re-sort on every render
  // Must be before early returns to satisfy rules-of-hooks.
  const groupedVulnerabilitiesPrintable = useMemo(
    () => groupVulnerabilities(reportData.vulnerabilityDetails),
    [reportData.vulnerabilityDetails]
  );

  if (loading) {
    return <PageSpinner className="print:hidden" />;
  }

  if (!reportData) {
    return (
      <PageEmptyState
        className="print:hidden"
        title="No Report Data Available"
        description="Run a scan to generate a printable report."
        action={
          <a href="/reports" className="text-primary hover:underline">
            Close
          </a>
        }
      />
    );
  }

  const handlePrint = () => window.print();

  const heatmapData = reportData.heatmapData || {
    aivss: [] as HeatmapShape['aivss'],
    cvss: [] as HeatmapShape['cvss'],
    aars: [] as HeatmapShape['aars'],
    vulnerability: [] as HeatmapShape['vulnerability'],
  };

  const hasScoreHeatmaps =
    heatmapData.aivss.length > 0 || heatmapData.cvss.length > 0 || heatmapData.aars.length > 0;

  const showHeatmapSection =
    (sectionVisibility.heatmapAivss && heatmapData.aivss.length > 0) ||
    (sectionVisibility.heatmapCvss && heatmapData.cvss.length > 0) ||
    (sectionVisibility.heatmapAars && heatmapData.aars.length > 0) ||
    (sectionVisibility.heatmapOverall && hasScoreHeatmaps) ||
    (sectionVisibility.heatmapVulnerability && heatmapData.vulnerability.length > 0);

  return (
    <div className="min-h-screen bg-[rgb(5,5,5)] text-zinc-100 print-container">
      <ReportSubHeader
        title="Print Preview"
        subtitle="AI Risk Assessment Report"
        actions={
          <>
            <Button onClick={handlePrint} className="gap-2">
              <Printer className="w-4 h-4" />
              Print / Save as PDF
            </Button>
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => {
                window.close();
                setTimeout(() => {
                  window.location.href = '/reports';
                }, 300);
              }}
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Reports
            </Button>
          </>
        }
      />

      <div className="max-w-6xl mx-auto px-6 py-8 print-content">
        {/* Report Header */}
        <div className="print-section mb-8">
          <ReportHeader
            reportId={reportData.reportId}
            reportDate={reportData.reportDate}
            pluginName={reportData.pluginName}
            branch={reportData.branch}
            classification={reportData.classification}
          />
        </div>

        {/* Executive Summary — matches ReportPage: RecommendationCard + 4 score cards */}
        {sectionVisibility.executiveSummary && (
          <section className="mb-12 print-section">
            <h2 className="text-2xl font-bold mb-6">Executive Summary</h2>
            <RecommendationCard
              type={reportData.recommendation.type}
              summary={reportData.recommendation.summary}
              overallScore={reportData.scores.overall}
            />
            <div className="grid md:grid-cols-4 gap-6 mt-6">
              <ScoreCard
                title="AIVSS Score"
                score={reportData.scores.aivss.score}
                riskLevel={reportData.scores.aivss.level}
                description="AI Vulnerability Scoring"
              />
              <ScoreCard
                title="CVSS Base Score"
                score={reportData.scores.cvss.score}
                riskLevel={reportData.scores.cvss.level}
                description="Common Vulnerability Score"
              />
              <ScoreCard
                title="AARS Score"
                score={reportData.scores.aars.score}
                maxScore={100}
                riskLevel={reportData.scores.aars.level}
                description="AI Agent Risk Score"
              />
              {reportData.scores.creatorTrust && (
                <ScoreCard
                  title="Creator Trust™"
                  score={reportData.scores.creatorTrust.score}
                  riskLevel={reportData.scores.creatorTrust.level}
                  description="Verified creator score"
                />
              )}
              {reportData.scores.crs && (
                <ScoreCard
                  title="SAFE™ Score"
                  score={reportData.scores.crs.score}
                  maxScore={1000}
                  riskLevel={reportData.scores.crs.level}
                  description="Composite Risk Score (higher is better)"
                />
              )}
            </div>
          </section>
        )}

        {/* Key Findings — VulnerabilitySummary always shown, not gated on keyFindings.length */}
        {sectionVisibility.keyFindings && (
          <section className="mb-12 print-section">
            <h2 className="text-2xl font-bold mb-6">Key Findings</h2>
            {reportData.keyFindings.length > 0 && (
              <div className="grid gap-3 mb-8">
                {reportData.keyFindings.map((finding: KeyFindingType, index: number) => (
                  <KeyFinding
                    key={index}
                    finding={finding.finding || ''}
                    status={finding.status || 'info'}
                    details={finding.details || ''}
                    linkedSection={finding.linkedSection}
                  />
                ))}
              </div>
            )}
            <VulnerabilitySummary counts={reportData.vulnerabilities} />
          </section>
        )}

        {/* Plugin Profile */}
        {sectionVisibility.pluginProfile && (
          <section className="mb-12 print-section">
            <h2 className="text-2xl font-bold mb-6">Plugin Profile</h2>
            <PluginProfile plugin={reportData.plugin} />
          </section>
        )}

        {/* Creator Verification */}
        {sectionVisibility.creatorVerification && (
          <section className="mb-12 print-section">
            <h2 className="text-2xl font-bold mb-6">VendorGuard (Creator Verification)</h2>
            <CreatorVerification creator={reportData.creator} />
          </section>
        )}

        {/* Scoring Breakdown — AARS gets maxScore={100} to match ReportPage */}
        {sectionVisibility.scoringBreakdown && (
          <section className="mb-12 print-section">
            <h2 className="text-2xl font-bold mb-6">Scoring Breakdown</h2>
            <div className="grid lg:grid-cols-3 gap-6">
              <ScoreBreakdown
                scoreType="AIVSS"
                totalScore={reportData.scores.aivss.score}
                riskLevel={reportData.scores.aivss.level}
                metrics={reportData.scoreBreakdowns.aivss}
              />
              <ScoreBreakdown
                scoreType="CVSS"
                totalScore={reportData.scores.cvss.score}
                riskLevel={reportData.scores.cvss.level}
                metrics={reportData.scoreBreakdowns.cvss}
              />
              <ScoreBreakdown
                scoreType="AARS"
                totalScore={reportData.scores.aars.score}
                maxScore={100}
                riskLevel={reportData.scores.aars.level}
                metrics={reportData.scoreBreakdowns.aars}
              />
            </div>
          </section>
        )}

        {/* Risk Heatmaps — all visible ones in a grid, including vulnerability (no separate section) */}
        {showHeatmapSection && (
          <section className="mb-12 print-section">
            <h2 className="text-2xl font-bold mb-6">Risk Assessment Heatmaps</h2>
            <div className="grid lg:grid-cols-2 gap-6">
              {sectionVisibility.heatmapAivss && heatmapData.aivss.length > 0 && (
                <RiskHeatmap
                  title="AIVSS Risk Heatmap"
                  subtitle="AI Vulnerability Severity Score breakdown"
                  dataPoints={heatmapData.aivss}
                />
              )}
              {sectionVisibility.heatmapCvss && heatmapData.cvss.length > 0 && (
                <RiskHeatmap
                  title="CVSS Risk Heatmap"
                  subtitle="Common Vulnerability Scoring System breakdown"
                  dataPoints={heatmapData.cvss}
                />
              )}
              {sectionVisibility.heatmapAars && heatmapData.aars.length > 0 && (
                <RiskHeatmap
                  title="AARS Risk Heatmap"
                  subtitle="AI Agent Risk Score breakdown"
                  dataPoints={heatmapData.aars}
                />
              )}
              {sectionVisibility.heatmapOverall && hasScoreHeatmaps && (
                <RiskHeatmap
                  title="Overall Risk Heatmap"
                  subtitle="Combined AIVSS, CVSS, and AARS risk assessment"
                  dataPoints={[
                    ...heatmapData.aivss,
                    ...heatmapData.cvss,
                    ...heatmapData.aars,
                  ]}
                />
              )}
              {sectionVisibility.heatmapVulnerability && heatmapData.vulnerability.length > 0 && (
                <RiskHeatmap
                  title="Vulnerability Risk Heatmap"
                  subtitle="Identified vulnerabilities mapped by likelihood and impact"
                  dataPoints={heatmapData.vulnerability}
                />
              )}
            </div>
          </section>
        )}

        {/* Rules File Integrity Analysis (RFIA) — shown only when scan included RFIA */}
        {!!(reportData.rfiaAggregate && reportData.rfiaAggregate.filesScanned > 0) &&
          sectionVisibility.rfiaSection && (
            <section className="mb-12 print-section">
              <h2 className="text-2xl font-bold mb-6">Rules File Integrity Analysis</h2>
              <RFIASection
                findings={reportData.rfiaFindings || []}
                aggregate={reportData.rfiaAggregate}
                defaultExpanded={true}
              />
            </section>
          )}

        {/* Vulnerability Assessment */}
        {sectionVisibility.vulnerabilityAssessment && reportData.vulnerabilityDetails.length > 0 && (
          <section className="mb-12 print-section">
            <h2 className="text-2xl font-bold mb-6">Vulnerability Assessment</h2>
            <div className="space-y-3">
              {/* GRTTL4C-122: groupedVulnerabilities memoized above */}
          {groupedVulnerabilitiesPrintable.map((group) => (
                <VulnerabilityGroup
                  key={group.title}
                  title={group.title}
                  vulnerabilities={group.vulnerabilities}
                  defaultExpanded={true}
                />
              ))}
            </div>
          </section>
        )}

        {/* Threat Modeling */}
        {sectionVisibility.threatModeling &&
          (reportData.threatModeling.threatActors.length > 0 ||
            reportData.threatModeling.attackVectors.length > 0) && (
            <section className="mb-12 print-section">
              <h2 className="text-2xl font-bold mb-6">Threat Modeling</h2>
              <ThreatModeling
                threatActors={reportData.threatModeling.threatActors}
                attackVectors={reportData.threatModeling.attackVectors}
                assetsCritical={reportData.threatModeling.assetsCritical}
              />
            </section>
          )}

        {/* Permission Scope Analysis — MCPToolsSummary + PermissionScope in one section */}
        {sectionVisibility.permissionScope &&
          (reportData.mcpTools.totalTools > 0 || reportData.permissions.length > 0) && (
            <section className="mb-12 print-section">
              <h2 className="text-2xl font-bold mb-6">Permission Scope Analysis</h2>
              {reportData.mcpTools.totalTools > 0 && (
                <MCPToolsSummary
                  totalTools={reportData.mcpTools.totalTools}
                  highRiskTools={reportData.mcpTools.highRiskTools}
                  mediumRiskTools={reportData.mcpTools.mediumRiskTools}
                  lowRiskTools={reportData.mcpTools.lowRiskTools}
                  toolsList={reportData.mcpTools.toolsList}
                  toolsInfo={reportData.mcpTools.toolsInfo}
                />
              )}
              {reportData.permissions.length > 0 && (
                <PermissionScope
                  permissions={reportData.permissions}
                  toolDetails={toolDetailsData}
                />
              )}
            </section>
          )}

        {/* Prioritized Remediation */}
        {sectionVisibility.prioritizedRemediation &&
          reportData.remediationItems?.length > 0 && (
            <section className="mb-12 print-section">
              <h2 className="text-2xl font-bold mb-6">Prioritized Remediation</h2>
              <PrioritizedRemediation items={reportData.remediationItems} />
            </section>
          )}

        {/* Risk Acceptance Matrix */}
        {sectionVisibility.riskAcceptanceMatrix &&
          reportData.riskAcceptanceItems?.length > 0 && (
            <section className="mb-12 print-section">
              <h2 className="text-2xl font-bold mb-6">Risk Acceptance Matrix</h2>
              <RiskAcceptanceMatrix items={reportData.riskAcceptanceItems} />
            </section>
          )}

        {/* Final Recommendation — with conditions (matches ReportPage bottom section) */}
        {sectionVisibility.recommendation && (
          <section className="mb-12 print-section">
            <h2 className="text-2xl font-bold mb-6">Final Recommendation</h2>
            <RecommendationCard
              type={reportData.recommendation.type}
              summary={reportData.recommendation.summary}
              overallScore={reportData.scores.overall}
              conditions={
                Array.isArray(reportData.recommendation.conditions)
                  ? reportData.recommendation.conditions
                  : []
              }
            />
          </section>
        )}

        <ReportFooter generatedDate={reportData.generatedAt} showPrintInfo={true} />
      </div>
    </div>
  );
};

export default PrintableReportPage;
